<template>
    <ReaderModal wide :fixed-height="false" :aria-label="strings.batchTitle" @close-modal="closeAddOnBatchDialog">
        <template #header>
            <h2 class="px-4 pb-1 pt-3 text-left font-semibold leading-6 text-theme-accent">{{ strings.batchTitle }}</h2>
        </template>

        <div
            class="flex w-full flex-col gap-4 rounded-lg p-4 pt-2"
            :class="{ 'ring-2 ring-inset ring-theme-accent': dragDepth > 0 }"
            dusk="AddOnsBatchDialog"
            @dragenter.prevent="dragDepth += plan ? 0 : 1"
            @dragover.prevent
            @dragleave="dragDepth = Math.max(dragDepth - 1, 0)"
            @drop.prevent="dropFile"
        >
            <template v-if="!plan">
                <p class="text-sm text-theme-text">{{ strings.batchIntro }}</p>

                <div class="flex min-w-0 items-center gap-2">
                    <button
                        type="button"
                        dusk="AddOnsBatchUpload"
                        :class="[classes.secondary, 'shrink-0 gap-1.5']"
                        :aria-describedby="[ids.drop, error && ids.error].filter(Boolean).join(' ')"
                        @click="$refs.file.click()"
                    >
                        <svg-icon icon="cloud-arrow-up" width="16px" height="16px" />
                        {{ strings.batchUpload }}
                    </button>
                    <span
                        :id="ids.drop"
                        class="min-w-0 truncate text-xs"
                        :class="fileName ? 'text-theme-text' : 'text-theme-toolbar-icon'"
                        >{{ fileName || strings.batchDropHint }}</span
                    >
                    <!-- The button above is the way in: this is only the browser's file picker. -->
                    <input
                        ref="file"
                        type="file"
                        accept=".xlsx,.csv,.tsv,.txt"
                        tabindex="-1"
                        aria-hidden="true"
                        class="hidden"
                        dusk="AddOnsBatchFile"
                        @change="chooseFile"
                    />
                </div>

                <div>
                    <label :for="ids.paste" class="text-sm font-medium text-theme-text">{{
                        strings.batchPasteLabel
                    }}</label>
                    <textarea
                        :id="ids.paste"
                        ref="paste"
                        v-model="text"
                        rows="6"
                        dusk="AddOnsBatchPaste"
                        :class="classes.paste"
                        :placeholder="strings.batchPastePlaceholder"
                        :aria-describedby="[ids.columns, error && ids.error].filter(Boolean).join(' ')"
                        @input="error = ''"
                        @paste="pasteTable"
                    ></textarea>
                    <p :id="ids.columns" class="mt-2 flex items-start gap-1.5 text-xs text-theme-toolbar-icon">
                        <svg-icon
                            class="mt-px shrink-0"
                            icon="outline-info-24px"
                            fill="currentColor"
                            width="14px"
                            height="14px"
                        />
                        <span
                            >{{ columnsHint.before
                            }}<span class="font-monospace text-theme-text">{{ columnsHint.columns }}</span
                            >{{ columnsHint.after }}</span
                        >
                    </p>
                </div>

                <p v-if="error" :id="ids.error" role="alert" class="text-sm text-theme-danger" dusk="AddOnsBatchError">
                    {{ error }}
                </p>

                <div class="flex justify-end gap-2">
                    <button type="button" :class="classes.secondary" @click="closeAddOnBatchDialog">
                        {{ strings.batchCancel }}
                    </button>
                    <button
                        type="button"
                        dusk="AddOnsBatchPreview"
                        :class="classes.primary"
                        :disabled="!text.trim()"
                        @click="readPaste"
                    >
                        {{ strings.batchRead }}
                    </button>
                </div>
            </template>

            <template v-else>
                <p
                    ref="summary"
                    tabindex="-1"
                    class="text-sm text-theme-text focus:outline-none"
                    dusk="AddOnsBatchSummary"
                >
                    {{ summary }}
                </p>
                <p v-if="plan.mapping.unrecognized.length" class="text-xs text-theme-toolbar-icon">
                    {{ unrecognizedText }}
                </p>

                <!-- Focusable so a keyboard can scroll a table wider or longer than the dialog. -->
                <div
                    role="region"
                    tabindex="0"
                    :aria-label="strings.batchCaption"
                    class="max-h-[50vh] overflow-auto rounded-lg border border-theme-border focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent"
                >
                    <table class="w-full border-collapse text-left text-xs text-theme-text" dusk="AddOnsBatchTable">
                        <caption class="sr-only">
                            {{
                                strings.batchCaption
                            }}
                        </caption>
                        <thead>
                            <tr class="border-b border-theme-border">
                                <th scope="col" :class="classes.headCell">{{ strings.batchColumnLine }}</th>
                                <th scope="col" :class="classes.headCell">{{ strings.batchColumnStatus }}</th>
                                <th v-for="column in columns" :key="column" scope="col" :class="classes.headCell">
                                    {{ columnLabel(column) }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="row in plan.rows"
                                :key="row.line"
                                :data-line="row.line"
                                class="border-b border-theme-border align-top last:border-b-0"
                                :class="{ 'bg-theme-danger-bg': !row.entry }"
                            >
                                <th scope="row" :class="[classes.cell, 'font-normal']">{{ row.line }}</th>
                                <td :class="[classes.cell, 'min-w-40']">
                                    <span
                                        :class="row.entry ? 'text-theme-text' : 'text-theme-danger'"
                                        v-text="statusOf(row)"
                                    />
                                    <span
                                        v-for="(warning, index) in warningsOf(row)"
                                        :key="index"
                                        class="block text-theme-toolbar-icon"
                                        v-text="warningText(warning)"
                                    />
                                    <button
                                        v-if="clampedLines.has(row.line)"
                                        type="button"
                                        dusk="AddOnsBatchRowToggle"
                                        :aria-expanded="String(expandedLines.has(row.line))"
                                        class="mt-1 block rounded text-theme-accent underline focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent"
                                        @click="toggleRow(row.line)"
                                    >
                                        {{ expandedLines.has(row.line) ? strings.batchShowLess : strings.batchShowAll
                                        }}<span class="sr-only">, {{ strings.batchColumnLine }} {{ row.line }}</span>
                                    </button>
                                </td>
                                <td v-for="column in columns" :key="column" :class="[classes.cell, 'max-w-60']">
                                    <span v-if="column === 'title'" class="flex items-start gap-1.5">
                                        <!-- The icon the piece gets, chosen or inferred, with its name for a screen reader. -->
                                        <span class="size-4 shrink-0" :title="typeLabel(row)">
                                            <svg-icon
                                                v-if="row.entry"
                                                class="text-theme-accent"
                                                :icon="addOnIcon(row.entry.record.type)"
                                                width="16px"
                                                height="16px"
                                            />
                                        </span>
                                        <span v-if="row.entry" class="sr-only">{{ typeLabel(row) }}</span>
                                        <span data-clamp :class="clampClass(row)">{{ row.cells.title }}</span>
                                        <span
                                            v-if="row.entry && row.insertedAs"
                                            dusk="AddOnsBatchInsertedAs"
                                            class="shrink-0 rounded-full bg-theme-accent-bg px-2 text-[10.5px] font-medium leading-4 text-theme-accent"
                                            >{{ insertedText(row) }}</span
                                        >
                                    </span>
                                    <span v-else data-clamp :class="clampClass(row)">{{ row.cells[column] }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <p class="text-xs text-theme-toolbar-icon">{{ strings.batchDraftsNote }}</p>

                <div class="flex justify-end gap-2">
                    <button type="button" dusk="AddOnsBatchBack" :class="classes.secondary" @click="back">
                        {{ strings.batchBack }}
                    </button>
                    <button
                        type="button"
                        dusk="AddOnsBatchConfirm"
                        :class="classes.primary"
                        :disabled="plan.entries.length === 0"
                        @click="confirm"
                    >
                        {{ confirmText }}
                    </button>
                </div>
            </template>
        </div>
    </ReaderModal>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapState } from 'vuex';
import ReaderModal from '../ReaderModal/ReaderModal.vue';
import { PILL_CLASSES } from '../Components/buttons/pillClasses';
import { addOnIcon } from './addOnIcon.js';
import { addOnStrings, addOnTypeLabel, fill } from './addOnStrings.js';
import { BATCH_COLUMNS } from './batch/batchColumns';
import { checkImages } from './batch/batchImages';
import { planBatch } from './batch/batchPlan';
import { tableReplacingPaste, toTabSeparated } from './batch/pastedTable';
import * as batchText from './batch/batchText';
import { parseDelimited, readSpreadsheetFile } from './batch/spreadsheet';

// Settings for a row rather than its content.
const HIDDEN_COLUMNS = ['type', 'insertAs'];

const CLASSES = {
    primary: PILL_CLASSES.primary,
    secondary: PILL_CLASSES.secondary,
    paste: 'mt-1 block w-full rounded-lg border border-theme-border bg-theme-bg px-3 py-2 font-monospace text-xs text-theme-text outline-none focus-visible:ring-2 focus-visible:ring-theme-accent',
    cell: 'px-2 py-1.5 font-medium',
    // Sticky on the cells: older WebViews do not stick a whole thead.
    headCell: 'sticky top-0 bg-theme-bg px-2 py-1.5 font-medium',
};

export default {
    components: { ReaderModal },
    data() {
        return {
            text: '',
            error: '',
            plan: null,
            fileName: '',
            // Entering a child fires its own dragenter before the parent's dragleave.
            dragDepth: 0,
            unreachableImages: new Set(),
            // The plan whose images were all tried, so the run need not try them again.
            imagesCheckedFor: null,
            // New Sets on each change, since Vue 2 does not see into one.
            clampedLines: new Set(),
            expandedLines: new Set(),
        };
    },
    computed: {
        ...mapState(['localizations', 'lang']),
        ...mapGetters(['addOnBatchContext']),
        strings() {
            return addOnStrings(this.localizations, this.lang);
        },
        classes() {
            return CLASSES;
        },
        ids() {
            const id = `add-on-batch-${this._uid}`;

            return { drop: `${id}-drop`, paste: `${id}-paste`, columns: `${id}-columns`, error: `${id}-error` };
        },
        /** The sentence around the column list, so the list can read as the headers to type. */
        columnsHint() {
            const [before, after = ''] = fill(this.strings.batchColumnsHint).split('{columns}');

            return { before, after, columns: BATCH_COLUMNS.map(column => this.columnLabel(column)).join(', ') };
        },
        /** The content columns the plan reads, in the sheet's own order. The type shows as the title's icon instead. */
        columns() {
            return this.plan.mapping.byIndex.filter(column => column && !HIDDEN_COLUMNS.includes(column));
        },
        summary() {
            const ready = this.plan.entries.length;

            return fill(this.strings.batchSummary, { ready, skipped: this.plan.rows.length - ready });
        },
        unrecognizedText() {
            return fill(this.strings.batchUnrecognized, { columns: this.plan.mapping.unrecognized.join(', ') });
        },
        confirmText() {
            return fill(this.strings.batchConfirm, { count: this.plan.entries.length });
        },
    },
    beforeDestroy() {
        this.imageCheck?.abort();
    },
    methods: {
        ...mapActions(['closeAddOnBatchDialog', 'startAddOnBatch']),
        addOnIcon,
        typeLabel(row) {
            return row.entry ? addOnTypeLabel(this.strings, row.entry.record.type) : '';
        },
        columnLabel(column) {
            return batchText.columnLabel(column, this.strings);
        },
        /** The plan's own warnings, and whether its image loaded here, which only the browser can say. */
        warningsOf(row) {
            const imageUrl = row.entry?.record.imageUrl;
            if (!imageUrl || !this.unreachableImages.has(imageUrl)) return row.warnings;

            return [...row.warnings, { code: 'imageUnreachable' }];
        },
        warningText(warning) {
            return batchText.warningText(warning, this.strings);
        },
        statusOf(row) {
            if (row.entry) return this.strings.batchReady;

            const reasons = row.errors.map(error => batchText.errorText(error, this.strings)).join('; ');
            return fill(this.strings.batchSkipped, { reason: reasons });
        },
        insertedText(row) {
            return row.insertedAs === 'embed' ? this.strings.batchInsertedEmbed : this.strings.batchInsertedLink;
        },
        chooseFile(event) {
            const [file] = event.target.files ?? [];
            // The same file chosen again after a fix must fire a change.
            event.target.value = '';
            if (file) this.readFile(file);
        },
        dropFile(event) {
            this.dragDepth = 0;
            const [file] = event.dataTransfer?.files ?? [];
            if (file && !this.plan) this.readFile(file);
        },
        async readFile(file) {
            this.fileName = file.name;
            this.error = '';
            try {
                await this.show(await readSpreadsheetFile(file));
            } catch (error) {
                this.error = batchText.fileErrorText(error, this.strings);
            }
        },
        /** A paste whose text split a row is taken from the table it carries, written back as text. */
        pasteTable(event) {
            const clipboard = event.clipboardData;
            const grid =
                clipboard && tableReplacingPaste(clipboard.getData('text/html'), clipboard.getData('text/plain'));
            if (!grid) return;

            event.preventDefault();
            const { value, selectionStart, selectionEnd } = event.target;
            const pasted = toTabSeparated(grid);
            this.text = value.slice(0, selectionStart) + pasted + value.slice(selectionEnd);
            this.error = '';
            this.$nextTick(() => {
                const caret = selectionStart + pasted.length;
                this.$refs.paste?.setSelectionRange(caret, caret);
            });
        },
        readPaste() {
            return this.show(parseDelimited(this.text));
        },
        async show(grid) {
            const plan = planBatch(grid, { ...this.addOnBatchContext, emptiesBody: await this.bodyEmptier() });
            if (plan.fatal) {
                this.error = batchText.fatalText(plan, this.strings);
                return;
            }

            this.error = '';
            // Frozen, so a sheet of hundreds of rows is not made reactive cell by cell.
            this.plan = Object.freeze(plan);
            this.$nextTick(() => {
                this.$refs.summary?.focus();
                this.findClampedRows();
            });
            this.checkImagesOf(plan);
        },
        /**
         * The editor's schema comes with the editor, so it loads on the first
         * preview only. Should it fail to load, the host still refuses the row.
         */
        async bodyEmptier() {
            this.emptiesBody ??= import('../../js/tiptap/emptiedAddOnBody')
                .then(({ addOnBodyEmptier }) => addOnBodyEmptier(this.addOnBatchContext.embedHosts))
                .catch(() => undefined);

            return this.emptiesBody;
        },
        /** The host downloads each image after the import, so a link that will fail is worth flagging before. */
        checkImagesOf(plan) {
            this.stopImageCheck();
            const check = new AbortController();
            this.imageCheck = check;

            const urls = plan.entries.map(entry => entry.record.imageUrl).filter(Boolean);
            checkImages(
                urls,
                found => {
                    // A new Set, since Vue 2 does not see into one.
                    this.unreachableImages = new Set([...this.unreachableImages, ...found]);
                },
                { signal: check.signal }
            ).then(() => {
                if (!check.signal.aborted) this.imagesCheckedFor = plan;
            });
        },
        stopImageCheck() {
            this.imageCheck?.abort();
            this.unreachableImages = new Set();
            this.imagesCheckedFor = null;
        },
        clampClass(row) {
            return ['break-words', this.expandedLines.has(row.line) ? 'whitespace-pre-wrap' : 'line-clamp-2'];
        },
        /** Only a row with a cell cut on this screen offers to show it whole. */
        findClampedRows() {
            const clamped = [...this.$el.querySelectorAll('[data-clamp]')]
                .filter(cell => cell.scrollHeight > cell.clientHeight)
                .map(cell => Number(cell.closest('tr').dataset.line));

            this.clampedLines = new Set(clamped);
        },
        toggleRow(line) {
            const expanded = new Set(this.expandedLines);
            if (!expanded.delete(line)) expanded.add(line);
            this.expandedLines = expanded;
        },
        back() {
            this.stopImageCheck();
            this.plan = null;
            this.clampedLines = new Set();
            this.expandedLines = new Set();
            this.$nextTick(() => this.$refs.paste?.focus());
        },
        confirm() {
            const { entries } = this.plan;
            // Read before closing, which stops the check and forgets its result.
            const unreachable = this.imagesCheckedFor === this.plan ? [...this.unreachableImages] : null;
            this.closeAddOnBatchDialog();
            this.startAddOnBatch({ entries, unreachable });
        },
    },
};
</script>
